import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL || '';

function EmailAutomation() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const defaultConfigs = [
    {
      name: 'contact',
      email_address: 'contact@viphandling.com',
      smtp_server: 'send.one.com',
      smtp_port: 465,
      password: '',
      use_ssl: true,
      bcc_address: 'contact@viphandling.com',
      use_for_flight_creation: false,
      use_for_flight_import: false,
      use_for_report_send: false,
      use_for_invoice_send: false,
      use_for_invoice_reminder: false,
      use_for_statement_send: false,
      use_for_payment_notification: false,
      use_for_user_registration: false,
      use_for_verification_code: false
    },
    {
      name: 'accounting',
      email_address: 'accounting@viphandling.com',
      smtp_server: 'send.one.com',
      smtp_port: 465,
      password: '',
      use_ssl: true,
      bcc_address: 'contact@viphandling.com',
      use_for_flight_creation: false,
      use_for_flight_import: false,
      use_for_report_send: false,
      use_for_invoice_send: false,
      use_for_invoice_reminder: false,
      use_for_statement_send: false,
      use_for_payment_notification: false,
      use_for_user_registration: false,
      use_for_verification_code: false
    },
    {
      name: 'admin',
      email_address: 'admin@viphandling.com',
      smtp_server: 'send.one.com',
      smtp_port: 465,
      password: '',
      use_ssl: true,
      bcc_address: 'contact@viphandling.com',
      use_for_flight_creation: false,
      use_for_flight_import: false,
      use_for_report_send: false,
      use_for_invoice_send: false,
      use_for_invoice_reminder: false,
      use_for_statement_send: false,
      use_for_payment_notification: false,
      use_for_user_registration: false,
      use_for_verification_code: false
    }
  ];

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/smtp-configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.length > 0) {
        const configMap = {};
        response.data.forEach(config => {
          configMap[config.name] = { ...config, password: '' };
        });

        const mergedConfigs = defaultConfigs.map(def => {
          return configMap[def.name] || def;
        });

        setConfigs(mergedConfigs);
      } else {
        setConfigs(defaultConfigs);
      }
    } catch (error) {
      console.error('Failed to fetch SMTP configs:', error);
      toast.error('Failed to load email configurations');
      setConfigs(defaultConfigs);
    } finally {
      setLoading(false);
    }
  };

  const handleMigration = async () => {
    if (!window.confirm('This will migrate your old email configuration to the new system. Continue?')) {
      return;
    }

    setMigrating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/smtp-configs/migrate-from-old`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.note) {
          toast.info(response.data.note, { autoClose: 8000 });
        }
        await fetchConfigs();
      } else {
        toast.error(response.data.message || 'Migration failed');
      }
    } catch (error) {
      console.error('Migration failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Migration failed';
      toast.error(errorMsg);
    } finally {
      setMigrating(false);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...configs];
    updated[index][field] = value;
    setConfigs(updated);
  };

  const handleSave = async (index) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = configs[index];

      if (config.id) {
        await axios.put(`${API}/api/smtp-configs/${config.id}`, config, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${config.name} configuration updated successfully`);
      } else {
        const response = await axios.post(`${API}/api/smtp-configs`, config, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${config.name} configuration created successfully`);
        
        const updated = [...configs];
        updated[index].id = response.data.id;
        setConfigs(updated);
      }

      await fetchConfigs();
    } catch (error) {
      console.error('Failed to save config:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save configuration';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (index) => {
    const testEmail = prompt('Enter email address to send test email:');
    if (!testEmail) return;

    try {
      const token = localStorage.getItem('token');
      const config = configs[index];

      if (!config.id) {
        toast.error('Please save the configuration first');
        return;
      }

      const response = await axios.post(
        `${API}/api/smtp-configs/${config.id}/test`,
        { test_email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Test email failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Test email failed';
      toast.error(errorMsg);
    }
  };

  const renderConfigCard = (config, index) => {
    const displayName = config.name.charAt(0).toUpperCase() + config.name.slice(1);
    
    return (
      <div key={config.name} style={{
        background: '#2A2E39',
        border: '1px solid #3A3E49',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ color: '#FFD700', marginBottom: '20px', fontSize: '20px' }}>
          {displayName} Email Configuration ({config.email_address})
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF' }}>
              Email Address *
            </label>
            <input
              type="email"
              value={config.email_address}
              onChange={(e) => handleChange(index, 'email_address', e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                background: '#1A1E29',
                border: '1px solid #3A3E49',
                borderRadius: '4px',
                color: '#FFFFFF'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF' }}>
              SMTP Server *
            </label>
            <input
              type="text"
              value={config.smtp_server}
              onChange={(e) => handleChange(index, 'smtp_server', e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                background: '#1A1E29',
                border: '1px solid #3A3E49',
                borderRadius: '4px',
                color: '#FFFFFF'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF' }}>
              SMTP Port *
            </label>
            <input
              type="number"
              value={config.smtp_port}
              onChange={(e) => handleChange(index, 'smtp_port', parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '10px',
                background: '#1A1E29',
                border: '1px solid #3A3E49',
                borderRadius: '4px',
                color: '#FFFFFF'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF' }}>
              Password * {config.has_password && <span style={{ color: '#4A9FFF' }}>(Encrypted)</span>}
            </label>
            <input
              type="password"
              value={config.password}
              onChange={(e) => handleChange(index, 'password', e.target.value)}
              placeholder={config.has_password ? "Enter new password to change" : "Enter password"}
              style={{
                width: '100%',
                padding: '10px',
                background: '#1A1E29',
                border: '1px solid #3A3E49',
                borderRadius: '4px',
                color: '#FFFFFF'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#FFFFFF' }}>
              BCC Address (Auto-included in all emails)
            </label>
            <input
              type="email"
              value={config.bcc_address || ''}
              onChange={(e) => handleChange(index, 'bcc_address', e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                background: '#1A1E29',
                border: '1px solid #3A3E49',
                borderRadius: '4px',
                color: '#FFFFFF'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_ssl}
                onChange={(e) => handleChange(index, 'use_ssl', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Use SSL (Port 465)
            </label>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #3A3E49', paddingTop: '20px', marginTop: '10px' }}>
          <h4 style={{ color: '#FFD700', marginBottom: '16px', fontSize: '16px' }}>
            Usage Rules - When to use this email configuration:
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_flight_creation}
                onChange={(e) => handleChange(index, 'use_for_flight_creation', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Flight Creation Notification
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_flight_import}
                onChange={(e) => handleChange(index, 'use_for_flight_import', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Flight Import Notification
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_report_send}
                onChange={(e) => handleChange(index, 'use_for_report_send', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Send Reports to Agency
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_invoice_send}
                onChange={(e) => handleChange(index, 'use_for_invoice_send', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Send Invoices
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_invoice_reminder}
                onChange={(e) => handleChange(index, 'use_for_invoice_reminder', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Send Invoice Reminders
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_statement_send}
                onChange={(e) => handleChange(index, 'use_for_statement_send', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Send Statements
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_payment_notification}
                onChange={(e) => handleChange(index, 'use_for_payment_notification', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Payment Marked by Agency
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_user_registration}
                onChange={(e) => handleChange(index, 'use_for_user_registration', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              New User Registration
            </label>

            <label style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={config.use_for_verification_code}
                onChange={(e) => handleChange(index, 'use_for_verification_code', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Verification Code Emails
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          <button
            onClick={() => handleSave(index)}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: '#4A9FFF',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {saving ? 'Saving...' : '💾 Save Configuration'}
          </button>

          {config.id && (
            <button
              onClick={() => handleTest(index)}
              style={{
                padding: '10px 20px',
                background: '#28A745',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📧 Send Test Email
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#FFFFFF' }}>
        <h2>Loading Email Automation Settings...</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', color: '#FFFFFF', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '28px', color: '#FFD700' }}>
          📧 Email Automation Configuration
        </h1>
        
        <button
          onClick={handleMigration}
          disabled={migrating}
          style={{
            padding: '10px 20px',
            background: '#FF9800',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '4px',
            cursor: migrating ? 'not-allowed' : 'pointer',
            fontWeight: 'bold'
          }}
        >
          {migrating ? 'Migrating...' : '🔄 Migrate Old Config'}
        </button>
      </div>

      <div style={{
        background: '#3A3E49',
        border: '1px solid #4A9FFF',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
      }}>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
          <strong>ℹ️ Information:</strong> Configure 3 separate SMTP email accounts for different purposes. 
          All passwords are encrypted and stored securely. Each configuration can have different usage rules. 
          The BCC field will auto-include your company email on all sent messages.
        </p>
      </div>

      {configs.map((config, index) => renderConfigCard(config, index))}
    </div>
  );
}

export default EmailAutomation;
